<script>
    import { ValueService } from '../../services/ValueService.js';

    /**
     * @type {{
     *     type?: string,
     *     name?: string | null,
     *     placeholder?: string | null,
     *     id?: string | null,
     *     label?: string | null,
     *     description?: string | null,
     *     autocomplete?: boolean | string,
     *     autocapitalize?: boolean,
     *     autocorrect?: boolean,
     *     autofocus?: boolean,
     *     required?: boolean,
     *     fullWidth?: boolean,
     *     value?: string,
     *     thisRef?: HTMLInputElement | HTMLTextAreaElement | null,
     *     [key: string]: any,
     * }}
     */
    let {
        type = 'text',
        name = null,
        placeholder = null,
        id = null,
        label = null,
        description = null,
        autocomplete = false,
        autocapitalize = false,
        autocorrect = false,
        autofocus = false,
        required = false,
        fullWidth = false,
        value = $bindable(''),
        thisRef = $bindable(null),
        ...restProps
    } = $props();

    /** @type {{ [key: string]: string }} */
    const allowedTypes = {
        text: 'text',
        password: 'password',
        area: 'textarea',
        date: 'date',
        datetime: 'datetime-local',
    };

    const useFullWidth = $derived(ValueService.toBool(fullWidth));

    const inputClass = $derived(`focus:bg-secondary-100 required:invalid:bg-warning-150 disabled:bg-black-50
        border-black-300 required:invalid:border-warning-500 disabled:border-black-200
        disabled:text-black-300 relative z-1 m-0
        block w-full ${useFullWidth ? '' : 'max-w-md'}
        rounded-sm
        border-1
        bg-(--dwc-input-bg-color) px-2
        py-1 text-(length:--dwc-input-font-size)
        outline-0
        focus:ring
        disabled:pointer-events-none`);

    // https://www.w3schools.com/tags/att_input_autocomplete.asp
    /** @type {{ [key: string]: string }} */
    const allowedAutoComplete = {
        'on': 'on',
        'off': 'off',
        'email': 'email',
        'url': 'url',
        'new-password': 'new-password',
        'current-password': 'current-password',
    };

    const useName = $derived(ValueService.toStringSingleLine(name));
    const usePlaceholder = $derived(
        ValueService.isDefined(placeholder) ? ValueService.toStringSingleLine(placeholder) : null
    );
    const useType = $derived(ValueService.toStringSingleLine(type));
    const useAutoComplete = $derived(
        autocomplete === true
            ? 'on'
            : ValueService.hasValue(autocomplete)
              ? ValueService.toStringSingleLine(autocomplete)
              : 'off'
    );
    const useId = $derived(
        ValueService.hasValue(id) ? ValueService.toStringSingleLine(id) : `ly-input-${crypto.randomUUID()}`
    );
    const useInputType = $derived(allowedTypes[useType] ?? 'text');
    const isTextarea = $derived(useInputType === 'textarea');
    const isDate = $derived(useInputType === 'date');
    const isDateTime = $derived(useInputType === 'datetime-local');
    const useDescription = $derived(ValueService.toStringSingleLine(description));
    const useDateValue = $derived(
        isDate ? ValueService.toDate(value) : isDateTime ? ValueService.toDateTimeLocal(value) : ''
    );

    /**
     * @type {{
     *     id: string,
     *     type?: string,
     *     name?: string,
     *     placeholder?: string,
     *     lang?: string,
     *     step?: number,
     *     autocomplete?: import("svelte/elements").FullAutoFill,
     *     autocapitalize: "on" | "off",
     *     autocorrect: "on" | "off",
     *     autofocus: boolean,
     *     required: boolean,
     * }}
     */
    const inputProps = $derived({
        id: useId,
        ...(isTextarea
            ? {
                  autocomplete: /** @type {import("svelte/elements").FullAutoFill} */ (
                      useAutoComplete !== 'on' ? 'off' : 'off'
                  ),
              }
            : {
                  type: useInputType,
                  autocomplete: /** @type {import("svelte/elements").FullAutoFill} */ (
                      allowedAutoComplete[useAutoComplete] ?? 'off'
                  ),
              }),
        ...(isDate || isDateTime
            ? {
                  lang: 'de-DE',
                  ...(isDateTime ? { step: 60 } : {}),
              }
            : {}),
        ...(useName !== '' ? { name: useName } : {}),
        ...(usePlaceholder !== null ? { placeholder: usePlaceholder } : {}),
        autocapitalize: ValueService.toBool(autocapitalize) ? 'on' : 'off',
        autocorrect: ValueService.toBool(autocorrect) ? 'on' : 'off',
        autofocus: ValueService.toBool(autofocus),
        required: ValueService.toBool(required),
    });

    /**
     * @param {Event & {currentTarget: EventTarget & HTMLInputElement}} event
     * @returns {void}
     */
    function onDateInput(event) {
        value = event.currentTarget.value;
    }

    /**
     * @param {HTMLElement | null} el
     * @returns {void}
     */
    function autoResizeTextarea(el) {
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    }

    /**
     * @param {Event & {currentTarget: EventTarget & HTMLTextAreaElement}} event
     * @returns {void}
     */
    function onTextareaInput(event) {
        autoResizeTextarea(event.currentTarget);
    }

    $effect(() => {
        void value;
        if (isTextarea) {
            autoResizeTextarea(thisRef);
        }
    });
</script>

<div class="lyInput relative w-full">
    {#if isTextarea}
        <textarea
            {...restProps}
            {...inputProps}
            bind:value
            bind:this={thisRef}
            oninput={onTextareaInput}
            rows="1"
            class="{inputClass} min-h-24 resize-y overflow-hidden"></textarea>
    {:else if isDate || isDateTime}
        <input
            {...restProps}
            {...inputProps}
            value={useDateValue}
            oninput={onDateInput}
            bind:this={thisRef}
            class={inputClass} />
    {:else}
        <input {...restProps} {...inputProps} bind:value bind:this={thisRef} class={inputClass} />
    {/if}
    {#if ValueService.isDefined(label)}
        <label
            for={useId}
            class="pointer-events-none absolute z-10 m-0
                   block rounded-sm
                   border border-(--dwc-border-color) bg-(--dwc-label-bg-color)
                   leading-none text-nowrap
                   transition-all
                   duration-150 ease-in-out">
            {ValueService.toStringSingleLine(label)}
        </label>
    {/if}
    {#if useDescription}
        <p class="text-black-300 m-0 block w-full {useFullWidth ? '' : 'max-w-md'} px-2 py-1 text-xs">
            {useDescription}
        </p>
    {/if}
</div>

<style>
    .lyInput {
        --dwc-input-bg-color: #fff;
        --dwc-input-font-size: 1rem;

        label,
        input::placeholder,
        textarea::placeholder {
            color: #666;
            opacity: 1;
            line-height: 1;
            font-weight: normal;
            font-style: normal;
            font-size: var(--dwc-input-font-size);
        }

        label {
            --dwc-label-bg-color: transparent;
            --dwc-border-color: transparent;
            left: calc(0.5rem + 1px);
            top: 0.5rem;
            max-width: calc(100% - 0.75rem);
            overflow: hidden;
            text-overflow: ellipsis;
            padding: 0;
            line-height: 1;
            height: 1.25rem;
        }

        input,
        textarea {
            &:not([placeholder='']),
            &:focus,
            &:not(:placeholder-shown),
            &[type='date'],
            &[type='datetime-local'] {
                + label {
                    --dwc-input-font-size: 0.65rem;
                    --dwc-label-bg-color: var(--dwc-input-bg-color);
                    --dwc-border-color: #666;
                    left: 0.3rem;
                    top: -0.65rem;
                    padding: 0.125rem 0.25rem;
                    height: auto;
                }
            }
        }
    }
</style>
